import { useState, useEffect } from 'react';
import { getShiftsHistory, type Shift } from '../../api/shifts.api';

import { useShift } from '../../hooks/useShift';
import { formatCOP } from '../../utils/currencyFormat';
import ShiftModals from '../../components/feature/shifts/ShiftModals';

export default function TurnosPage() {
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const { shift: activeShift, loading: loadingActive } = useShift();
    
    // Estado para modal de cierre de caja por admin
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [selectedShift, setSelectedShift] = useState<Shift | null>(null);

    useEffect(() => {
        loadShifts();
    }, []);

    const loadShifts = async () => {
        try {
            const data = await getShiftsHistory();
            setShifts(data);
        } catch (error) {
            console.error('Error loading shifts:', error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleOpenCloseModal = (shift: Shift) => {
        setSelectedShift(shift);
        setShowCloseModal(true);
    };

    const handleCloseSuccess = () => {
        setShowCloseModal(false);
        setSelectedShift(null);
        loadShifts();
    };

    const userRole = localStorage.getItem('userRole') || 'OPERADOR';
    const isAdmin = userRole === 'ADMIN' || userRole === 'SUPERADMIN';
    const isOperator = userRole === 'OPERADOR';

    // Filtrar turnos abiertos para el dashboard de Admin
    const openShifts = shifts.filter(s => s.status === 'ABIERTO');

    return (
        <>
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-800">Control de Turnos</h1>
                <p className="text-slate-500">Gestión de caja y auditoría de operaciones.</p>
            </div>

            {/* SECCIÓN ADMIN: Cajas Abiertas */}
            {isAdmin && (
                <div className="mb-8">
                    <h2 className="text-lg font-bold text-slate-800 mb-4">Cajas Abiertas ({openShifts.length})</h2>
                    
                    {loadingHistory ? (
                         <div className="p-8 text-center text-slate-500 bg-white rounded-xl border border-slate-200">
                            <i className="ri-loader-4-line animate-spin text-2xl"></i>
                         </div>
                    ) : openShifts.length === 0 ? (
                        <div className="p-8 text-center text-slate-500 bg-white rounded-xl border border-slate-200">
                            No hay cajas abiertas actualmente.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {openShifts.map(shift => (
                                <div key={shift.id} className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 border-l-4 border-l-green-500">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            {/* @ts-ignore */}
                                            <h3 className="font-bold text-slate-800">{shift.operador?.nombre || 'Operador'}</h3>
                                            <p className="text-xs text-slate-500">{shift.numero}</p>
                                        </div>
                                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-lg">
                                            ABIERTO
                                        </span>
                                    </div>
                                    
                                    <div className="space-y-2 mt-4">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Base:</span>
                                            <span className="font-medium text-slate-700">{formatCOP(shift.montoInicial)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Gastos:</span>
                                            <span className="font-medium text-red-600">{formatCOP(shift.gastos || 0)}</span>
                                        </div>
                                        {(shift.ventasTienda ?? 0) > 0 && (
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500">Ventas tienda:</span>
                                                <span className="font-medium text-purple-700">{formatCOP(shift.ventasTienda)}</span>
                                            </div>
                                        )}
                                        <div className="pt-2 border-t border-slate-100 flex justify-between items-center">
                                            <span className="text-slate-600 font-bold">Recaudo:</span>
                                            <span className="text-lg font-bold text-blue-600">{formatCOP(shift.total)}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="mt-3 text-xs text-slate-400 flex items-center gap-1">
                                        <i className="ri-time-line"></i>
                                        Abierto: {new Date(shift.apertura).toLocaleString()}
                                    </div>

                                    <button
                                        onClick={() => handleOpenCloseModal(shift)}
                                        className="w-full mt-4 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
                                    >
                                        <i className="ri-close-circle-line"></i>
                                        Cerrar Caja
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* SECCIÓN OPERADOR: Turno Activo Card */}
            {isOperator && (
            <div className="mb-6 sm:mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                 {/* Estado Card */}
                 <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
                            <i className="ri-store-2-line text-xl"></i>
                        </div>
                        {!loadingActive && (
                            <span className={`px-2 py-1 rounded-lg text-xs font-bold ${activeShift ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {activeShift ? 'ABIERTO' : 'CERRADO'}
                            </span>
                        )}
                    </div>
                    {activeShift ? (
                        <div>
                            <p className="text-sm text-slate-500 mb-1">Turno Actual</p>
                            <h3 className="text-2xl font-bold text-slate-800 mb-2">{activeShift.numero}</h3>
                            <p className="text-xs text-slate-500">
                                <i className="ri-time-line mr-1"></i>
                                {new Date(activeShift.apertura).toLocaleString()}
                            </p>
                        </div>
                    ) : (
                        <div>
                            <p className="text-sm text-slate-500 mb-1">Estado</p>
                            <h3 className="text-xl font-bold text-slate-400">Sin turno activo</h3>
                            <p className="text-xs text-slate-400 mt-2">Debe abrir caja para operar.</p>
                        </div>
                    )}
                 </div>

                 {/* Base Inicial Card */}
                 <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                            <i className="ri-safe-2-line text-xl"></i>
                        </div>
                    </div>
                    <div>
                        <p className="text-sm text-slate-500 mb-1">Base de Caja</p>
                        <h3 className="text-2xl font-bold text-slate-800">
                            {activeShift ? formatCOP(activeShift.montoInicial) : '$0'}
                        </h3>
                        <p className="text-xs text-slate-500 mt-2">Monto inicial para cambios.</p>
                    </div>
                 </div>

                 {/* Operador Card */}
                 <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
                            <i className="ri-user-star-line text-xl"></i>
                        </div>
                    </div>
                    <div>
                        <p className="text-sm text-slate-500 mb-1">Operador Responsable</p>
                        <h3 className="text-lg font-bold text-slate-800 truncate">
                            {localStorage.getItem('userName')?.toUpperCase() || 'USUARIO'}
                        </h3>
                        <p className="text-xs text-slate-500 mt-2">
                           ID: {localStorage.getItem('userId')?.slice(0,8)}...
                        </p>
                    </div>
                 </div>
            </div>
            )}

            <div className="mb-4">
                <h2 className="text-lg font-bold text-slate-800">Historial Reciente</h2>
            </div>


            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Turno / Operador</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Apertura</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Cierre</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Base</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Gastos</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Recaudo</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Diferencia</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">Estado</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loadingHistory ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                                        <i className="ri-loader-4-line animate-spin text-2xl"></i>
                                    </td>
                                </tr>
                            ) : shifts.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                                        No hay turnos registrados.
                                    </td>
                                </tr>
                            ) : (
                                shifts.map((shift) => (
                                    <tr key={shift.id} className="hover:bg-slate-50/50">
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-slate-800">{shift.numero}</div>
                                            {/* @ts-ignore */}
                                            <div className="text-xs text-slate-500">{shift.operador?.nombre || 'Operador'}</div>
                                            {shift.sede && <div className="text-xs text-teal-600">{shift.sede.nombre}</div>}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600">
                                            {new Date(shift.apertura).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600">
                                            {shift.cierre ? new Date(shift.cierre).toLocaleString() : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-right text-slate-600">
                                            {formatCOP(shift.montoInicial)}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-right text-red-600">
                                            {formatCOP(shift.gastos || 0)}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-bold text-right text-slate-800">
                                            {formatCOP(shift.total)}
                                        </td>
                                        <td className={`px-6 py-4 text-sm font-bold text-right ${shift.diferencia < 0 ? 'text-red-500' : shift.diferencia > 0 ? 'text-green-500' : 'text-slate-400'}`}>
                                            {shift.diferencia !== 0 && shift.diferencia ? formatCOP(shift.diferencia) : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${shift.status === 'ABIERTO' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                                {shift.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de Cierre usando ShiftModals */}
            <ShiftModals
                isOpenModalOpen={false}
                isCloseModalOpen={showCloseModal}
                onClose={() => {
                    setShowCloseModal(false);
                    setSelectedShift(null);
                }}
                onOpenShift={async () => false}
                onCloseShift={async () => {}}
                currentShift={selectedShift || undefined}
                shiftId={selectedShift?.id}
                onCloseSuccess={handleCloseSuccess}
            />
        </>
    );
}
